interface InvoiceCursorPagerProps {
  itemCount: number;
  page: number;
  canGoPrev: boolean;
  hasMore: boolean;
  onPrev: () => void;
  onNext: () => void;
  /** True while a page request is in flight and stale placeholder rows are still shown. */
  pageLoading?: boolean;
  /** e.g. find-in-table hint shown after row count */
  detail?: string;
  /** Tighter padding for download list pages. */
  compact?: boolean;
}

export function InvoiceCursorPager({
  itemCount,
  page,
  canGoPrev,
  hasMore,
  onPrev,
  onNext,
  pageLoading = false,
  detail,
  compact = false,
}: InvoiceCursorPagerProps) {
  const handlePrev = () => {
    if (!canGoPrev || pageLoading) return;
    onPrev();
  };

  const handleNext = () => {
    if (!hasMore || pageLoading) return;
    onNext();
  };

  return (
    <div
      className={`invoice-cursor-pager flex flex-wrap items-center justify-between border-t border-[var(--border-soft)] bg-[var(--surface-2)] text-[var(--muted)] ${
        compact
          ? "invoice-cursor-pager--compact gap-2 px-3 py-1.5 text-xs"
          : "gap-3 px-4 py-2 text-sm"
      }`}
      aria-busy={pageLoading || undefined}
    >
      <span>
        Showing{" "}
        <span className="tabular-nums font-semibold text-[var(--text)]">
          {itemCount}
        </span>{" "}
        on this page
        {detail ? <span className="text-xs"> {detail}</span> : null}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="pager-nav-btn"
          disabled={!canGoPrev || pageLoading}
          onClick={handlePrev}
          aria-label="Previous page"
        >
          ← Prev
        </button>
        <span
          className="min-w-[5.5rem] text-center tabular-nums"
          aria-live="polite"
          aria-label={`Page ${page}${hasMore ? ", more pages available" : ""}`}
        >
          <span className="font-semibold text-[var(--text)]">Page {page}</span>
          {hasMore ? (
            <span className="ml-1 text-xs font-normal text-[var(--muted)]">+</span>
          ) : null}
        </span>
        {pageLoading ? (
          <span className="pager-refresh-hint" aria-live="polite">
            Refreshing…
          </span>
        ) : null}
        <button
          type="button"
          className="pager-nav-btn"
          disabled={!hasMore || pageLoading}
          onClick={handleNext}
          aria-label={pageLoading ? "Loading page" : "Next page"}
        >
          Next →
        </button>
      </div>
    </div>
  );
}
